import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatSnakeCaseToTitle } from '@/lib/utils';
import {
  AdminPageHeader,
  AdminSheet,
  AdminEmptyState,
  AdminTableSkeleton,
  DeleteConfirmDialog,
} from './shared';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Plus,
  Upload,
  Download,
  Pencil,
  Trash2,
  ChevronsUpDown,
  FolderPlus,
  ListFilter,
} from 'lucide-react';
import { SearchInput } from '@/components/shared/SearchInput';

interface LookupItem {
  id: string;
  lookup_group: string;
  value: string;
  display_name: string;
  description: string | null;
  display_order: number | null;
  is_active: boolean;
  parent_lookup_group: string | null;
  parent_value: string | null;
}

interface LookupGroup {
  name: string;
  count: number;
  items: LookupItem[];
  hasChildren: boolean;
}

const initialFormData = {
  value: '',
  display_name: '',
  description: '',
  display_order: 0,
  is_active: true,
  parent_lookup_group: '',
  parent_value: '',
};


export function LookupsManager() {
  const { toast } = useToast();
  const [lookups, setLookups] = useState<LookupItem[]>([]);
  const [groups, setGroups] = useState<LookupGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<LookupItem | null>(null);
  const [editingGroup, setEditingGroup] = useState<string>('');
  const [formData, setFormData] = useState(initialFormData);
  const [isSaving, setIsSaving] = useState(false);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<LookupItem | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);

  // New group dialog
  const [newGroupDialogOpen, setNewGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // Rename group dialog
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [groupToRename, setGroupToRename] = useState<string>('');
  const [newGroupNameForRename, setNewGroupNameForRename] = useState('');

  // CSV Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importTargetGroup, setImportTargetGroup] = useState('');
  const [csvData, setCsvData] = useState<Array<{ value: string; display_name: string; description?: string }>>([]);
  const [csvError, setCsvError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from('lookups')
      .select('*')
      .order('lookup_group')
      .order('display_order');

    if (error) {
      toast({ title: 'Error fetching lookups', description: error.message, variant: 'destructive' });
    } else if (data) {
      setLookups(data);

      // Group by lookup_group
      const groupMap = new Map<string, LookupItem[]>();
      data.forEach((item) => {
        const existing = groupMap.get(item.lookup_group) || [];
        existing.push(item);
        groupMap.set(item.lookup_group, existing);
      });

      // Find parent references
      const parentGroups = new Set(data.map((d) => d.parent_lookup_group).filter(Boolean));

      const groupsList: LookupGroup[] = Array.from(groupMap.entries())
        .map(([name, items]) => ({
          name,
          count: items.length,
          items,
          hasChildren: parentGroups.has(name),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setGroups(groupsList);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredGroups = useMemo(() => {
    if (!searchQuery) return groups;

    const query = searchQuery.toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.value.toLowerCase().includes(query) ||
            item.display_name.toLowerCase().includes(query) ||
            item.description?.toLowerCase().includes(query) ||
            group.name.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.items.length > 0 || group.name.toLowerCase().includes(query));
  }, [groups, searchQuery]);

  const getParentValues = (parentGroup: string) => {
    const group = groups.find((g) => g.name === parentGroup);
    return group?.items || [];
  };

  // Handlers
  const openAddValue = (groupName: string) => {
    setEditingItem(null);
    setEditingGroup(groupName);
    setFormData({ ...initialFormData, display_order: groups.find((g) => g.name === groupName)?.items.length || 0 });
    setEditorOpen(true);
  };

  const openEditValue = (item: LookupItem) => {
    setEditingItem(item);
    setEditingGroup(item.lookup_group);
    setFormData({
      value: item.value,
      display_name: item.display_name,
      description: item.description || '',
      display_order: item.display_order || 0,
      is_active: item.is_active,
      parent_lookup_group: item.parent_lookup_group || '',
      parent_value: item.parent_value || '',
    });
    setEditorOpen(true);
  };

  const handleSaveValue = async () => {
    if (!formData.value || !formData.display_name) {
      toast({ title: 'Missing required fields', variant: 'destructive' });
      return;
    }

    setIsSaving(true);

    const payload = {
      lookup_group: editingGroup,
      value: formData.value,
      display_name: formData.display_name,
      description: formData.description || null,
      display_order: formData.display_order,
      is_active: formData.is_active,
      parent_lookup_group: formData.parent_lookup_group || null,
      parent_value: formData.parent_value || null,
    };

    if (editingItem) {
      const { error } = await supabase.from('lookups').update(payload).eq('id', editingItem.id);

      if (error) {
        toast({ title: 'Error updating lookup', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Lookup updated' });
        setEditorOpen(false);
        fetchData();
      }
    } else {
      const { error } = await supabase.from('lookups').insert(payload);

      if (error) {
        toast({ title: 'Error creating lookup', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Lookup created' });
        setEditorOpen(false);
        fetchData();
      }
    }

    setIsSaving(false);
  };

  const toggleActive = async (item: LookupItem, isActive: boolean) => {
    const { error } = await supabase.from('lookups').update({ is_active: isActive }).eq('id', item.id);

    if (error) {
      toast({ title: 'Error updating status', description: error.message, variant: 'destructive' });
    } else {
      fetchData();
    }
  };

  const confirmDeleteValue = (item: LookupItem) => {
    setItemToDelete(item);
    setGroupToDelete(null);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteGroup = (groupName: string) => {
    setItemToDelete(null);
    setGroupToDelete(groupName);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (itemToDelete) {
      const { error } = await supabase.from('lookups').delete().eq('id', itemToDelete.id);

      if (error) {
        toast({ title: 'Error deleting lookup', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Lookup deleted' });
        fetchData();
      }
    } else if (groupToDelete) {
      const { error } = await supabase.from('lookups').delete().eq('lookup_group', groupToDelete);

      if (error) {
        toast({ title: 'Error deleting group', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Group deleted' });
        fetchData();
      }
    }

    setDeleteDialogOpen(false);
    setItemToDelete(null);
    setGroupToDelete(null);
  };

  // New group
  const handleCreateGroup = () => {
    if (!newGroupName.trim()) return;

    const slugifiedName = newGroupName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');

    setNewGroupDialogOpen(false);
    setNewGroupName('');
    openAddValue(slugifiedName);
  };

  // Rename group
  const openRenameGroup = (groupName: string) => {
    setGroupToRename(groupName);
    setNewGroupNameForRename(groupName);
    setRenameDialogOpen(true);
  };

  const handleRenameGroup = async () => {
    if (!newGroupNameForRename.trim() || newGroupNameForRename === groupToRename) {
      setRenameDialogOpen(false);
      return;
    }

    const slugifiedName = newGroupNameForRename
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');

    const { error } = await supabase
      .from('lookups')
      .update({ lookup_group: slugifiedName })
      .eq('lookup_group', groupToRename);

    if (error) {
      toast({ title: 'Error renaming group', description: error.message, variant: 'destructive' });
      return;
    }

    // Update parent references
    await supabase
      .from('lookups')
      .update({ parent_lookup_group: slugifiedName })
      .eq('parent_lookup_group', groupToRename);

    toast({ title: 'Group renamed' });
    setRenameDialogOpen(false);
    fetchData();
  };

  // CSV Import
  const openImport = (groupName: string) => {
    setImportTargetGroup(groupName);
    setCsvData([]);
    setCsvError(null);
    setImportDialogOpen(true);
  };

  const handleCsvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter((l) => l.trim());

      if (lines.length < 2) {
        setCsvError('CSV must have headers and at least one data row');
        return;
      }

      const headers = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/"/g, ''));

      if (!headers.includes('value') || !headers.includes('display_name')) {
        setCsvError('CSV must have "value" and "display_name" columns');
        return;
      }

      const valueIdx = headers.indexOf('value');
      const displayIdx = headers.indexOf('display_name');
      const descIdx = headers.indexOf('description');

      const parsed = lines
        .slice(1)
        .map((line) => {
          const cols = line.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
          return {
            value: cols[valueIdx],
            display_name: cols[displayIdx],
            description: descIdx >= 0 ? cols[descIdx] : undefined,
          };
        })
        .filter((row) => row.value && row.display_name);

      setCsvData(parsed);
      setCsvError(null);
    };
    reader.readAsText(file);
  };

  const confirmCsvImport = async () => {
    if (!importTargetGroup || csvData.length === 0) return;

    const existingItems = groups.find((g) => g.name === importTargetGroup)?.items.length || 0;

    const payload = csvData.map((row, idx) => ({
      lookup_group: importTargetGroup,
      value: row.value,
      display_name: row.display_name,
      description: row.description || null,
      display_order: existingItems + idx,
      is_active: true,
    }));

    const { error } = await supabase.from('lookups').insert(payload);

    if (error) {
      toast({ title: 'Import failed', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: `Imported ${csvData.length} values` });
      setImportDialogOpen(false);
      setCsvData([]);
      fetchData();
    }
  };

  // Export group
  const exportGroup = (groupName: string) => {
    const group = groups.find((g) => g.name === groupName);
    if (!group) return;

    const csvContent = [
      'value,display_name,description',
      ...group.items.map(
        (item) => `"${item.value}","${item.display_name}","${item.description || ''}"`
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${groupName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const expandAll = () => {
    setExpandedGroups(groups.map((g) => g.name));
  };

  const collapseAll = () => {
    setExpandedGroups([]);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <AdminPageHeader title="Lookups" count={0} onAdd={() => {}} addLabel="New Group" />
        <AdminTableSkeleton columns={5} rows={8} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Lookups"
        count={lookups.length}
        onAdd={() => setNewGroupDialogOpen(true)}
        addLabel="New Group"
      />

      {/* Search & Actions */}
      <div className="flex gap-3 flex-wrap">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search lookups..."
          className="flex-1 min-w-[200px]"
        />
        <Button variant="outline" onClick={expandedGroups.length > 0 ? collapseAll : expandAll}>
          <ChevronsUpDown className="h-4 w-4 mr-2" />
          {expandedGroups.length > 0 ? 'Collapse All' : 'Expand All'}
        </Button>
      </div>

      {filteredGroups.length === 0 ? (
        <AdminEmptyState
          icon={ListFilter}
          title="No lookup groups"
          description="Create a new lookup group to get started."
          actionLabel="New Group"
          onAction={() => setNewGroupDialogOpen(true)}
        />
      ) : (
        <Accordion
          type="multiple"
          value={expandedGroups}
          onValueChange={setExpandedGroups}
          className="space-y-2"
        >
          {filteredGroups.map((group) => (
            <AccordionItem
              key={group.name}
              value={group.name}
              className="border rounded-lg px-4"
            >
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center gap-3 flex-1">
                  <span className="font-medium">{formatSnakeCaseToTitle(group.name)}</span>
                  <Badge variant="secondary">{group.count}</Badge>
                  {group.hasChildren && (
                    <Badge variant="outline" className="text-xs">
                      Has dependents
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                {/* Group Actions Bar */}
                <div className="flex gap-2 mb-3 pb-3 border-b flex-wrap">
                  <Button size="sm" onClick={() => openAddValue(group.name)}>
                    <Plus className="h-4 w-4 mr-1" /> Add Value
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openImport(group.name)}>
                    <Upload className="h-4 w-4 mr-1" /> Import CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => exportGroup(group.name)}>
                    <Download className="h-4 w-4 mr-1" /> Export
                  </Button>
                  <div className="flex-1" />
                  <Button variant="ghost" size="sm" onClick={() => openRenameGroup(group.name)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => confirmDeleteGroup(group.name)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {/* Values Table */}
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Value</TableHead>
                        <TableHead>Display Name</TableHead>
                        <TableHead className="hidden md:table-cell w-40">Description</TableHead>
                        <TableHead className="w-20">Active</TableHead>
                        <TableHead className="hidden lg:table-cell w-32">Parent</TableHead>
                        <TableHead className="w-24 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.items.map((item) => (
                        <TableRow 
                          key={item.id}
                          className="cursor-pointer"
                          onClick={() => openEditValue(item)}
                        >
                          <TableCell>
                            <Badge variant="outline" className="font-mono text-xs">
                              {item.value}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{item.display_name}</TableCell>
                          <TableCell className="hidden md:table-cell text-muted-foreground text-sm truncate max-w-40">
                            {item.description || '—'}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Switch
                              checked={item.is_active}
                              onCheckedChange={(v) => toggleActive(item, v)}
                            />
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            {item.parent_lookup_group ? (
                              <span className="text-xs text-muted-foreground">
                                {formatSnakeCaseToTitle(item.parent_lookup_group)}: {item.parent_value}
                              </span>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" onClick={() => openEditValue(item)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => confirmDeleteValue(item)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Value Editor Sheet */}
      <AdminSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        title={editingItem ? 'Edit Lookup Value' : 'Add Lookup Value'}
        description={`Group: ${formatSnakeCaseToTitle(editingGroup)}`}
        onSave={handleSaveValue}
        isSaving={isSaving}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Value *</Label>
              <Input
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="e.g., sedan"
              />
              <p className="text-xs text-muted-foreground">The actual value stored in the database</p>
            </div>
            <div className="space-y-2">
              <Label>Display Name *</Label>
              <Input
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="e.g., Sedan"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional context..."
              rows={2}
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Parent Lookup (Optional)</Label>
            <p className="text-xs text-muted-foreground mb-2">
              For hierarchical lookups like Vehicle Models depending on Vehicle Makes
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Select
                value={formData.parent_lookup_group || 'none'}
                onValueChange={(v) =>
                  setFormData({ ...formData, parent_lookup_group: v === 'none' ? '' : v, parent_value: '' })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {groups
                    .filter((g) => g.name !== editingGroup)
                    .map((g) => (
                      <SelectItem key={g.name} value={g.name}>
                        {formatSnakeCaseToTitle(g.name)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              {formData.parent_lookup_group && (
                <Select
                  value={formData.parent_value}
                  onValueChange={(v) => setFormData({ ...formData, parent_value: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select parent value" />
                  </SelectTrigger>
                  <SelectContent>
                    {getParentValues(formData.parent_lookup_group).map((v) => (
                      <SelectItem key={v.value} value={v.value}>
                        {v.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="space-y-2 flex-1">
              <Label>Display Order</Label>
              <Input
                type="number"
                value={formData.display_order}
                onChange={(e) =>
                  setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })
                }
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch
                id="is-active"
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
              <Label htmlFor="is-active">Active</Label>
            </div>
          </div>
        </div>
      </AdminSheet>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        itemType={groupToDelete ? 'group' : 'lookup'}
        itemName={
          groupToDelete
            ? `${formatSnakeCaseToTitle(groupToDelete)} (${groups.find((g) => g.name === groupToDelete)?.count || 0} values)`
            : itemToDelete?.display_name
        }
        warning={
          groupToDelete && groups.find((g) => g.name === groupToDelete)?.hasChildren
            ? 'Warning: Other lookups depend on this group as a parent.'
            : undefined
        }
      />

      {/* New Group Dialog */}
      <Dialog open={newGroupDialogOpen} onOpenChange={setNewGroupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Lookup Group</DialogTitle>
            <DialogDescription>
              Enter a name for the new lookup group. It will be converted to snake_case.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="e.g., Vehicle Colors"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
              />
              {newGroupName && (
                <p className="text-xs text-muted-foreground">
                  Will be saved as:{' '}
                  <code className="bg-muted px-1 rounded">
                    {newGroupName
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '_')
                      .replace(/^_|_$/g, '')}
                  </code>
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewGroupDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateGroup} disabled={!newGroupName.trim()}>
              <FolderPlus className="h-4 w-4 mr-2" />
              Create & Add Value
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Group Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Lookup Group</DialogTitle>
            <DialogDescription>Enter a new name for this lookup group.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Group Name</Label>
              <Input
                value={newGroupNameForRename}
                onChange={(e) => setNewGroupNameForRename(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleRenameGroup()}
              />
              {newGroupNameForRename && newGroupNameForRename !== groupToRename && (
                <p className="text-xs text-muted-foreground">
                  Will be saved as:{' '}
                  <code className="bg-muted px-1 rounded">
                    {newGroupNameForRename
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '_')
                      .replace(/^_|_$/g, '')}
                  </code>
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRenameGroup}>Rename</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import CSV</DialogTitle>
            <DialogDescription>
              Import values to "{formatSnakeCaseToTitle(importTargetGroup)}" from a CSV file. Required
              columns: value, display_name. Optional: description.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select CSV File</Label>
              <Input type="file" accept=".csv" onChange={handleCsvFileChange} />
            </div>

            {csvError && <p className="text-sm text-destructive">{csvError}</p>}

            {csvData.length > 0 && (
              <div className="space-y-2">
                <Label>Preview ({csvData.length} rows)</Label>
                <div className="border rounded-md max-h-48 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Value</TableHead>
                        <TableHead>Display Name</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvData.slice(0, 10).map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-xs">{row.value}</TableCell>
                          <TableCell>{row.display_name}</TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {row.description || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {csvData.length > 10 && (
                  <p className="text-xs text-muted-foreground">
                    Showing first 10 of {csvData.length} rows
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmCsvImport} disabled={csvData.length === 0}>
              <Upload className="h-4 w-4 mr-2" />
              Import {csvData.length} Values
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
